import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { createMockableHook } from '@repo/common/utils/createMockableHook';
import { useCallback, useState } from 'react';
import {
	useAIEmbeddedTerminate,
	useAuthUser,
	useBulkGenerateAiMetadata,
	useStreamingAIPrompt,
} from '../../api';
import { useStreamingContent } from '../../hooks/useStreamingContent';

interface UseDescriptionEditorAiProps {
	entityId?: string;
	onKeep: (value: string) => void;
	refreshEditor: () => void;
}

function useDescriptionEditorAiInternal({
	entityId,
	onKeep,
	refreshEditor,
}: UseDescriptionEditorAiProps) {
	const { user, workspace } = useAuthUser();
	const [completedContent, setCompletedContent] = useState<string>('');

	const { mutateAsync: generateAIDescription } = useBulkGenerateAiMetadata();
	const { mutateAsync: terminateAIEmbedded } = useAIEmbeddedTerminate();

	const {
		isStreaming,
		hasAIGeneratedText: isActivePrompt,
		pollingId,
		setPollingId,
		activePrompt,
		setActivePrompt,
	} = useStreamingAIPrompt({
		onFinishStreaming: (prompt) => {
			if (prompt.response?.content) {
				setCompletedContent(prompt.response.content);
			}
		},
	});

	// Use activePrompt content if available, otherwise use saved completed content
	const aiGeneratedDescription =
		activePrompt?.response?.content ?? completedContent;
	// Consider AI text available if we have active prompt OR completed content
	const hasAIGeneratedText = isActivePrompt || !!completedContent;

	const generateDescription = useCallback(async () => {
		if (!entityId) {
			return;
		}

		// Reset any previously completed content before starting a new generation
		setCompletedContent('');

		try {
			const { jobId } = await generateAIDescription({
				data: {
					entity_ids: [entityId],
					field: 'description',
				},
			});

			setPollingId(jobId);
			setActivePrompt(null);
			refreshEditor();
		} catch (error) {
			showNotification({
				title: 'Error generating description',
				message: 'If the issue persists, please contact support',
				color: 'red',
			});
		}
	}, [
		entityId,
		generateAIDescription,
		refreshEditor,
		setActivePrompt,
		setPollingId,
	]);

	const stopAi = useCallback(async () => {
		if (!pollingId) {
			return;
		}

		await terminateAIEmbedded({
			id: pollingId,
		});

		setPollingId(null);
		setActivePrompt(null);
		setCompletedContent('');
		refreshEditor();
	}, [
		pollingId,
		refreshEditor,
		setActivePrompt,
		setPollingId,
		terminateAIEmbedded,
	]);

	const keepDescription = useCallback(() => {
		if (!entityId) {
			return;
		}

		trackEvent(
			'ai/description/keep',
			{
				entityId,
			},
			user,
			workspace
		);

		onKeep(aiGeneratedDescription);
		setPollingId(null);
		setActivePrompt(null);
		setCompletedContent('');
	}, [
		aiGeneratedDescription,
		entityId,
		onKeep,
		setActivePrompt,
		setPollingId,
		user,
		workspace,
	]);

	const rejectDescription = useCallback(() => {
		if (!entityId) {
			return;
		}

		trackEvent(
			'ai/description/reject',
			{
				entityId,
			},
			user,
			workspace
		);

		setPollingId(null);
		setActivePrompt(null);
		setCompletedContent('');
		refreshEditor();
	}, [entityId, refreshEditor, setActivePrompt, setPollingId, user, workspace]);

	const aiGeneratedDescriptionWithStreaming = useStreamingContent(
		isStreaming,
		aiGeneratedDescription,
		refreshEditor
	);

	return {
		isStreaming,
		hasAIGeneratedText,
		aiGeneratedDescription: aiGeneratedDescriptionWithStreaming,
		generateDescription,
		rejectDescription,
		keepDescription,
		stopAi,
	};
}

export const [useDescriptionEditorAi, MockUseDescriptionEditorAi] =
	createMockableHook(useDescriptionEditorAiInternal);
